// Simple utility for direct API calls to backend
import config from "@/config/config";

const API_BASE_URL = config.NEXT_PUBLIC_API_URL;

// Helper function to get token from cookies (client-side)
function getTokenFromCookies() {
  if (typeof document === "undefined") return null;

  const value = `; ${document.cookie}`;

  // Get token cookie (used for both admin and member)
  const parts = value.split(`; token=`);
  if (parts.length === 2) {
    return parts.pop().split(";").shift();
  }

  return null;
}

/**
 * Make an API call with upload progress support
 * @param {string} endpoint - API endpoint
 * @param {Object} options - Options including onProgress callback
 * @returns {Promise} - Response data
 */
export const makeApiCallWithProgress = async (endpoint, options = {}) => {
  const url = `${API_BASE_URL}${endpoint}`;
  const { onProgress, ...fetchOptions } = options;

  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();

    // Set up progress tracking
    if (onProgress && xhr.upload) {
      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) {
          const percentComplete = Math.round((event.loaded / event.total) * 100);
          onProgress(percentComplete);
        }
      };
    }

    // Set up response handlers
    xhr.onload = () => {
      try {
        if (xhr.status >= 200 && xhr.status < 300) {
          const data = JSON.parse(xhr.responseText);
          resolve(data);
        } else {
          let errorData;
          try {
            errorData = JSON.parse(xhr.responseText);
          } catch (parseError) {
            errorData = {
              message: `HTTP ${xhr.status}: ${xhr.statusText}`,
            };
          }
          reject(new Error(errorData.message || `HTTP error! status: ${xhr.status}`));
        }
      } catch (error) {
        reject(error);
      }
    };

    xhr.onerror = () => {
      reject(new Error('Network error occurred'));
    };

    xhr.ontimeout = () => {
      reject(new Error('Request timeout'));
    };

    // Open the request
    xhr.open(fetchOptions.method || 'POST', url);

    // Enable credentials for cookies
    xhr.withCredentials = true;

    // Set headers (except Content-Type for FormData)
    const token = getTokenFromCookies();
    if (token) {
      xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    }

    // Don't set Content-Type for FormData - let browser set it with boundary
    if (!(fetchOptions.body instanceof FormData)) {
      xhr.setRequestHeader('Content-Type', 'application/json');
    }

    // Send the request
    xhr.send(fetchOptions.body);
  });
};

/**
 * Make a direct API call to the backend
 * @param {string} endpoint - API endpoint (e.g., '/api/admin/posts')
 * @param {Object} options - Fetch options
 * @returns {Promise} - Response data
 */
export const makeApiCall = async (endpoint, options = {}) => {
  const url = `${API_BASE_URL}${endpoint}`;
  const { responseType = 'json', ...fetchOptions } = options;

  const defaultOptions = {
    headers: {
      "Content-Type": "application/json",
    },
  };

  // Get token from cookies and add Authorization header
  const token = getTokenFromCookies();
  if (token) {
    defaultOptions.headers["Authorization"] = `Bearer ${token}`;
  }

  const mergedOptions = {
    ...defaultOptions,
    ...fetchOptions,
    headers: {
      ...defaultOptions.headers,
      ...fetchOptions.headers,
    },
  };

  // Don't add Content-Type for FormData
  if (fetchOptions.body instanceof FormData) {
    delete mergedOptions.headers["Content-Type"];
  }

  try {
    const response = await fetch(url, mergedOptions);

    if (!response.ok) {
      let errorData;
      try {
        errorData = await response.json();
      } catch (parseError) {
        errorData = {
          message: `HTTP ${response.status}: ${response.statusText}`,
        };
      }

      if (config.NODE_ENV === "development") {



      }

      // For conflict errors (409) and validation errors (400), return the error response instead of throwing
      if (response.status === 409 || (response.status === 400 && errorData.code)) {
        return errorData;
      }

      const error = new Error(
        errorData.message || `HTTP error! status: ${response.status}`
      );
      error.details = errorData;
      throw error;
    }

    // Handle different response types
    if (responseType === 'blob') {
      return await response.blob();
    }

    const data = await response.json();
    return data;
  } catch (error) {
    if (config.NODE_ENV === "development") {




    }
    throw error;
  }
};

// Quick helper functions for common HTTP methods
export const apiGet = (endpoint, options = {}) =>
  makeApiCall(endpoint, { ...options, method: "GET" });

export const apiPost = (endpoint, data, options = {}) =>
  makeApiCall(endpoint, {
    ...options,
    method: "POST",
    body: data instanceof FormData ? data : JSON.stringify(data),
  });

export const apiPut = (endpoint, data, options = {}) =>
  makeApiCall(endpoint, {
    ...options,
    method: "PUT",
    body: data instanceof FormData ? data : JSON.stringify(data),
  });

export const apiDelete = (endpoint, options = {}) =>
  makeApiCall(endpoint, { ...options, method: "DELETE" });
